import type { OperatorRunStepWire } from '~/hooks/queries/use-operator-runs';

/**
 * FRONT-1996 "Take the wheel": a `human_takeover` step's `label` is exactly
 * "You were in control" (a hand-back after the takeover went active) or
 * "Astra asked for help — you let it continue" (a hand-back while it was
 * still merely `requested` — nobody ever actually took the keyboard) — see
 * `src/operator/loop.ts`'s `awaitHandBack`. The copy this ticket wants on
 * screen is "You were in control · 1m 12s" — the duration folded into the
 * label line. Only the "You were in control" case carries a duration worth
 * surfacing this way; the never-active case is rendered as its bare label.
 *
 * Shared by BOTH surfaces that render a transcript of steps
 * (`operator-transcript.tsx`'s panel pane and `desktop-task-card.tsx`'s
 * `DesktopTaskStepRow`) so the wording can never drift between them
 * (FRONT-1996 review M9).
 *
 * Reads the step's own STRUCTURED `takeoverDurationMs` field (added to
 * `OperatorRunStepWire` for FRONT-1996 F14 — see `src/operator/store.ts`'s
 * `takeoverStepFields`) rather than parsing the human-readable `note`
 * sentence ("Paused for 1m 12s."), which is independent prose meant for
 * `awaitHandBack`'s own purposes and may change wording without notice.
 *
 * The `'You were in control'` match below is the ONE literal both sides
 * must agree on — `src/operator/loop.ts`'s `awaitHandBack` writes it as the
 * step `label` (see that function's `wasActive` branch, pinned by
 * `src/operator/loop.test.ts`'s "pauses at the top of a round..." test).
 * There is no shared import across the backend/frontend split, so a future
 * copyedit of that string on the backend side is caught only if THIS
 * file's own test (`human-takeover-step-label.test.ts`, which pins the
 * exact literal) is also updated — nothing forces the two edits together.
 */
export function humanTakeoverStepLabel(step: OperatorRunStepWire): string {
  if (step.label !== 'You were in control' || step.takeoverDurationMs == null) return step.label;
  return `${step.label} · ${formatTakeoverDuration(step.takeoverDurationMs)}`;
}

/** Mirrors the backend's `formatPauseDuration` (`src/operator/loop.ts`) byte-for-byte
 *  so the duration reads identically wherever it is written. */
function formatTakeoverDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
}
